import { useMemo, useState } from 'react'
import { useStore } from '../store/store'
import type { BetResult, OddsQuote, SportsbookMarketType } from '../domain/types'
import {
  americanToDecimal,
  closingLineValue,
  decimalToImplied,
  expectedValue,
  noVigTwoWay,
  recommendedStake,
} from '../lib/math'
import { dailyRiskUsedPct, lossesInRow } from '../store/selectors'
import { fmtCurrency, fmtPct, fmtPctValue, fmtSigned } from '../lib/format'
import { Banner, Button, Card, NumberField, PageHead, Pill, SelectField, TextField } from '../components/ui'
import { ActionPill } from '../components/widgets'

export function BettingOdds() {
  const store = useStore()
  const odds = store.odds
  const risk = store.risk
  const dailyUsed = useMemo(() => dailyRiskUsedPct(store), [store])
  const streak = useMemo(() => lossesInRow(store.journal), [store.journal])

  const fairProbFor = (o: OddsQuote): number | null => {
    if (!o.correlationGroup) return null
    const siblings = odds.filter((x) => x.correlationGroup === o.correlationGroup)
    if (siblings.length !== 2) return null
    const other = siblings.find((x) => x.id !== o.id)
    if (!other) return null
    const fair = noVigTwoWay(o.decimalOdds, other.decimalOdds)
    return fair.a
  }

  return (
    <div className="stack">
      <PageHead title="Betting Odds (legal markets only)">
        Manual or imported odds across decimal, American and fractional. EdgeLab removes the vig, compares your model to the market, and sizes with fractional Kelly — only where it's legal for you.
      </PageHead>

      <Banner tone={dailyUsed >= 70 || streak >= risk.cooldownLossesTrigger ? 'danger' : 'info'}>
        <strong>Guardrails.</strong> Daily loss budget used: <span className="mono">{fmtPctValue(dailyUsed, 0)}</span>.
        Loss streak: <span className="mono">{streak}</span>{streak >= risk.cooldownLossesTrigger ? ' — cooldown triggered. Stop for today.' : '.'} Bet only legal markets in {store.meta.jurisdiction}.
      </Banner>

      <Card title="Odds board" subtitle="Set your probability to unlock EV, no-vig fair price and a sized stake">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Event</th><th>Pick</th><th>Sport</th><th>Decimal</th><th>Implied</th><th>No-vig</th><th>Your prob</th><th>EV/$1</th><th>Kelly stake</th><th>Action</th><th></th></tr>
            </thead>
            <tbody>
              {odds.map((o) => {
                const implied = decimalToImplied(o.decimalOdds)
                const fair = fairProbFor(o)
                const hasModel = o.modelProb !== undefined
                const ev = hasModel ? expectedValue({ modelProb: o.modelProb as number, decimalOdds: o.decimalOdds, minEdge: risk.minEdge }) : null
                const stake = hasModel ? recommendedStake({ bankroll: risk.bankroll, prob: o.modelProb as number, decimalOdds: o.decimalOdds, mode: risk.kellyMode, maxStakePct: risk.maxSinglePositionPct }) : null
                return (
                  <tr key={o.id}>
                    <td>{o.event}</td>
                    <td>{o.selection}</td>
                    <td><span className="tag">{o.sport}</span></td>
                    <td className="mono">{o.decimalOdds.toFixed(2)} <span className="faint">({o.americanOdds > 0 ? '+' : ''}{o.americanOdds})</span></td>
                    <td className="mono">{fmtPct(implied, 0)}</td>
                    <td className="mono">{fair !== null ? fmtPct(fair, 0) : '—'}</td>
                    <td style={{ maxWidth: 90 }}>
                      <input className="input mono" type="number" step={0.01} min={0} max={1} value={o.modelProb ?? ''} placeholder="—" onChange={(e) => store.setOddsModelProb(o.id, e.target.value === '' ? undefined : Number(e.target.value))} />
                    </td>
                    <td className={`mono ${ev ? (ev.evPerUnit >= 0 ? 'pos' : 'neg') : 'muted'}`}>{ev ? fmtSigned(ev.evPerUnit, 3) : '—'}</td>
                    <td className="mono">{stake ? fmtCurrency(stake.stake) : '—'}</td>
                    <td>{ev ? <ActionPill action={ev.decision} /> : <Pill tone="gray">set prob</Pill>}</td>
                    <td>
                      <Button size="sm" disabled={!ev || ev.decision === 'skip'} onClick={() => store.addBetIdea({ kind: 'sportsbook', refId: o.id, title: `${o.event} — ${o.selection}`, sport: o.sport, modelProb: o.modelProb as number, decimalOdds: o.decimalOdds, stake: stake?.stake ?? 0, feePct: 0, status: 'watch', paper: true, tags: [o.sport], correlationGroup: o.correlationGroup, closingDecimal: o.closingDecimal, result: 'pending' })}>
                        Track
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <TrackedBets />
      <div className="grid cols-2">
        <ManualOddsEntry />
        <ParlayCalculator />
      </div>
    </div>
  )
}

function TrackedBets() {
  const betIdeas = useStore((s) => s.betIdeas)
  const updateBetIdea = useStore((s) => s.updateBetIdea)
  const removeBetIdea = useStore((s) => s.removeBetIdea)

  if (betIdeas.length === 0) return <Card title="Tracked bets"><div className="empty">No tracked bets yet. Use "Track" on the odds board.</div></Card>

  return (
    <Card title="Tracked bets" subtitle="Record results and closing-line value. Beating the close is the best long-run signal you have an edge.">
      <div className="table-wrap">
        <table className="table">
          <thead><tr><th>Bet</th><th>Odds</th><th>Stake</th><th>CLV</th><th>Result</th><th></th></tr></thead>
          <tbody>
            {betIdeas.map((b) => {
              const clv = b.closingDecimal ? closingLineValue(b.decimalOdds, b.closingDecimal) : null
              return (
                <tr key={b.id}>
                  <td>{b.title}{b.paper && <> <Pill tone="blue">paper</Pill></>}</td>
                  <td className="mono">{b.decimalOdds.toFixed(2)}</td>
                  <td className="mono">{fmtCurrency(b.stake)}</td>
                  <td className="mono">{clv ? <span className={clv.beatClose ? 'pos' : 'neg'}>{fmtSigned(clv.clvPct, 1)}%</span> : '—'}</td>
                  <td>
                    <select value={b.result} onChange={(e) => updateBetIdea(b.id, { result: e.target.value as BetResult })} style={{ maxWidth: 120 }}>
                      <option value="pending">pending</option>
                      <option value="win">win</option>
                      <option value="loss">loss</option>
                      <option value="push">push</option>
                    </select>
                  </td>
                  <td><button className="btn sm danger" onClick={() => removeBetIdea(b.id)}>Remove</button></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

function ManualOddsEntry() {
  const addOddsQuotes = useStore((s) => s.addOddsQuotes)
  const [event, setEvent] = useState('')
  const [selection, setSelection] = useState('')
  const [sport, setSport] = useState('NBA')
  const [marketType, setMarketType] = useState<SportsbookMarketType>('moneyline')
  const [american, setAmerican] = useState(-110)
  const [closing, setClosing] = useState(0)

  const add = () => {
    if (!event || !selection) return
    addOddsQuotes([
      {
        event,
        selection,
        sport,
        marketType,
        format: 'american',
        decimalOdds: americanToDecimal(american),
        americanOdds: american,
        book: 'Manual',
        timestamp: new Date().toISOString(),
        closingDecimal: closing > 1 ? closing : undefined,
        legal: true,
      },
    ])
    setEvent('')
    setSelection('')
  }

  return (
    <Card title="Manual odds entry" subtitle="Legal markets only">
      <div className="grid cols-2">
        <TextField label="Event" value={event} onChange={setEvent} placeholder="Team A vs Team B" />
        <TextField label="Selection" value={selection} onChange={setSelection} placeholder="Team A" />
        <TextField label="Sport" value={sport} onChange={setSport} />
        <SelectField label="Market" value={marketType} onChange={setMarketType} options={[{ value: 'moneyline', label: 'Moneyline' }, { value: 'spread', label: 'Spread' }, { value: 'total', label: 'Total' }, { value: 'prop', label: 'Prop' }]} />
        <NumberField label="American odds" value={american} onChange={setAmerican} step={5} />
        <NumberField label="Closing decimal (optional)" value={closing} onChange={setClosing} step={0.01} />
      </div>
      <Button variant="primary" onClick={add} disabled={!event || !selection}>Add odds</Button>
    </Card>
  )
}

function ParlayCalculator() {
  const [legs, setLegs] = useState<Array<{ odds: number; prob: number }>>([
    { odds: 1.91, prob: 0.55 },
    { odds: 1.91, prob: 0.55 },
  ])
  const combinedOdds = legs.reduce((a, l) => a * l.odds, 1)
  const combinedProb = legs.reduce((a, l) => a * l.prob, 1)
  const ev = expectedValue({ modelProb: combinedProb, decimalOdds: combinedOdds })
  const setLeg = (i: number, patch: Partial<{ odds: number; prob: number }>) => setLegs((l) => l.map((x, idx) => (idx === i ? { ...x, ...patch } : x)))

  return (
    <Card
      title="Parlay risk checker"
      subtitle="Parlays multiply the vig at every leg. They look exciting and almost always carry a worse edge."
      action={<div className="btn-row"><Button size="sm" onClick={() => setLegs((l) => [...l, { odds: 1.91, prob: 0.55 }])}>+ Leg</Button>{legs.length > 2 && <Button size="sm" onClick={() => setLegs((l) => l.slice(0, -1))}>− Leg</Button>}</div>}
    >
      {legs.map((l, i) => (
        <div className="row" key={i} style={{ gap: 10 }}>
          <NumberField label={`Leg ${i + 1} odds`} value={l.odds} onChange={(v) => setLeg(i, { odds: v })} step={0.01} />
          <NumberField label="Your prob" value={l.prob} onChange={(v) => setLeg(i, { prob: v })} step={0.01} min={0} max={1} />
        </div>
      ))}
      <div className="kv"><span className="k">Combined odds</span><span className="v">{combinedOdds.toFixed(2)}</span></div>
      <div className="kv"><span className="k">Combined win prob</span><span className="v">{fmtPct(combinedProb, 1)}</span></div>
      <div className="kv"><span className="k">EV per $1</span><span className={`v ${ev.evPerUnit >= 0 ? 'pos' : 'neg'}`}>{fmtSigned(ev.evPerUnit, 3)}</span></div>
      <Banner tone="warn">A {legs.length}-leg parlay needs all legs to hit. One miss and the whole ticket loses. Compounding correlation and vig usually makes singles the smarter play.</Banner>
    </Card>
  )
}
